import React from 'react';
import agentService from '../../../_services/agent.service';
import { AGENT_VISIBLE } from '../../../config/featureFlags';
import { EXTRA_FIELDS, normaliseAppearance, firstCut, isNewCut, wearable } from './appearance';

/**
 * The style chair's newer fields: hair technique, nail finish, tattoos and
 * the makeup parts (appearance.js EXTRA_FIELDS), and the newer cuts (the
 * buzz, the afro, box braids and the rest), which the API's hairStyle
 * doesn't take yet: a save sends the nearest first-version cut, and the
 * real one is kept here as `hairCut`.
 *
 * The server's word wins: once the API returns any of them, it stores them,
 * and they go with every save. Until then they are left out of the request
 * (the API validates the look and may reject fields it doesn't know), and on
 * staging only they are kept in this browser, per user, so the chair can be
 * demoed. Those don't follow the owner to another device. Where the Bailey
 * tab is visible at all is already staging and below (AGENT_VISIBLE).
 * The contract is in docs/BAILEY_STYLE_CHAIR_BACKEND.md.
 */
const LOCAL_OK = AGENT_VISIBLE;
const keyFor = () => {
    try { return `bly-look-extras:${(JSON.parse(localStorage.getItem('user')) || {})._id || ''}`; } catch (e) { return 'bly-look-extras:'; }
};

export const serverHasExtras = appearance => !!appearance && typeof appearance === 'object'
    && EXTRA_FIELDS.some(key => Object.prototype.hasOwnProperty.call(appearance, key));
/** Whether the chair can offer tattoos, finishes, technique and makeup parts. */
export const extrasAvailable = appearance => serverHasExtras(appearance) || LOCAL_OK;

export function pickExtras(look) {
    const out = {};
    EXTRA_FIELDS.forEach(key => { if (look && look[key] !== undefined) out[key] = look[key]; });
    return out;
}
function readLocalExtras() {
    if (!LOCAL_OK) return {};
    try { return JSON.parse(localStorage.getItem(keyFor()) || '{}') || {}; } catch (e) { return {}; }
}
function writeLocalExtras(extras) {
    if (!LOCAL_OK) return;
    try { localStorage.setItem(keyFor(), JSON.stringify(extras)); } catch (e) { /* private mode: they last the visit */ }
}

// A newer cut kept here stands in for the server's cut only while the
// server still has the first-version cut it was saved as; a different cut
// there (saved from another device) wins.
function withCut(raw, extras) {
    const { hairCut, ...rest } = extras || {};
    const server = raw && raw.hairStyle;
    return { ...raw, ...rest, ...(hairCut && isNewCut(hairCut) && firstCut(hairCut) === server ? { hairStyle: hairCut } : {}) };
}

/** Bailey's look from a status or save response, or null when there is none. */
export function resolveAppearance(data) {
    const raw = data && (data.appearance || (data.look ? { look: data.look } : null));
    if (!raw) return null;
    // A parked cut (appearance.js) shows as its first-version stand-in
    if (serverHasExtras(raw)) return wearable(normaliseAppearance(raw));
    return wearable(normaliseAppearance(withCut(typeof raw === 'string' ? { look: raw } : raw, readLocalExtras())));
}

/** The request body for a save: without the newer fields and cuts until the API stores them. */
export function savePayload(look, serverStoresExtras) {
    if (serverStoresExtras) return look;
    const out = { ...look };
    EXTRA_FIELDS.forEach(key => { delete out[key]; });
    if (out.hairStyle) out.hairStyle = firstCut(out.hairStyle);
    return out;
}

/** The look as saved: the server's answer, plus the newer fields kept locally when it has none. */
export function afterSave(responseAppearance, sent) {
    if (serverHasExtras(responseAppearance)) return normaliseAppearance(responseAppearance);
    const look = normaliseAppearance(sent);
    const extras = { ...pickExtras(look), ...(isNewCut(look.hairStyle) ? { hairCut: look.hairStyle } : {}) };
    writeLocalExtras(extras);
    const server = { ...(responseAppearance || {}) };
    if (!server.hairStyle) server.hairStyle = firstCut(look.hairStyle);
    return normaliseAppearance(LOCAL_OK ? withCut(server, extras) : server);
}

/*
 * The saved look for small places that show Bailey (the diary's setup chip),
 * read once per visit and shared, so each doesn't ask the server again.
 * The style chair calls rememberLook after a save, so they follow at once.
 */
// The last look seen is kept on this device too, so Bailey opens in it
// straight away instead of popping from the default look once the server
// answers. The server's answer still wins when it comes.
const lookKey = () => {
    try { return `bly-look:${(JSON.parse(localStorage.getItem('user')) || {})._id || ''}`; } catch (e) { return 'bly-look:'; }
};
export function cachedLook() {
    try {
        const raw = JSON.parse(localStorage.getItem(lookKey()) || 'null');
        return raw ? wearable(normaliseAppearance(raw)) : null;
    } catch (e) { return null; }
}
function cacheLook(look) {
    try {
        if (look) localStorage.setItem(lookKey(), JSON.stringify(look));
        else localStorage.removeItem(lookKey());
    } catch (e) { /* private mode: the default look until the server answers */ }
}

let sharedLook = null;
let sharedAsk = null;
const lookListeners = new Set();

export function rememberLook(look) {
    sharedLook = look || null;
    cacheLook(sharedLook);
    lookListeners.forEach(fn => fn(sharedLook));
}

export function useSavedLook() {
    const [look, setLook] = React.useState(() => sharedLook || cachedLook());
    React.useEffect(() => {
        lookListeners.add(setLook);
        if (!sharedLook && !sharedAsk) {
            sharedAsk = agentService.status()
                .then(res => { const saved = resolveAppearance((res && res.data && res.data.responseData) || {}); if (saved) rememberLook(saved); })
                .catch(() => { sharedAsk = null; /* the default Bailey; asked again next time */ });
        }
        if (sharedLook) setLook(sharedLook);
        return () => { lookListeners.delete(setLook); };
    }, []);
    return look;
}
